/**
 * Contact Management Table — Academic Curator
 * Shows contacts for a specific student. Receives studentId from route params
 * or from the logged-in student's own claims.
 */

import { useState, useMemo, useEffect, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppAuth, useIsAdmin } from '@/lib/mock-auth';
import { useEntityName } from '@/lib/EntityContext';
import { useContacts } from '@/lib/hooks/useContacts';
import { ContactCard } from '../components/ContactCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Plus, Download, Filter, Pencil, Star, Trash2 } from 'lucide-react';
import { CONTACT_TYPE_NAMES, TYPE_ICONS, TYPE_COLORS, groupByType } from '@/lib/contact-types';

import { type EditingContact } from '../components/ContactSlideOver';

interface ContactsListPageProps {
  onAddContact: () => void;
  onEditContact: (contact: EditingContact) => void;
  refreshKey?: number;
}

export function ContactsListPage({ onAddContact, onEditContact, refreshKey }: ContactsListPageProps) {
  const auth = useAppAuth();
  const isAdmin = useIsAdmin();
  const { studentId: paramStudentId, entityId: paramEntityId } = useParams<{
    studentId: string;
    entityId: string;
  }>();

  const studentId = paramStudentId || (auth.user?.profile?.['custom:studentId'] as string) || (auth.user?.profile?.sub as string) || '';
  const { contacts, student, entity, loading, error, refetch, deleteContact, setPrimaryContact } = useContacts(studentId, paramEntityId);

  // Re-fetch contacts when the parent signals a save (e.g. slide-over create)
  useEffect(() => {
    if (refreshKey) refetch();
  }, [refreshKey, refetch]);
  const entityName = useEntityName(paramEntityId) || entity?.name;
  const studentName = student ? `${student.firstName} ${student.lastName}` : 'My';

  const [typeFilter, setTypeFilter] = useState<number | null>(null);
  const [activeOnly, setActiveOnly] = useState(false);
  const [bouncedOnly, setBouncedOnly] = useState(false);

  const filteredContacts = contacts
    .filter((c) => !typeFilter || c.typeId === typeFilter)
    .filter((c) => !activeOnly || c.isActive)
    .filter((c) => !bouncedOnly || c.bounced)
    .sort((a, b) => a.typeId - b.typeId || a.rank - b.rank);

  const groupedContacts = useMemo(() => groupByType(filteredContacts), [filteredContacts]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4" role="alert">
        <p className="text-destructive text-sm font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center text-[11px] font-bold tracking-widest text-muted-foreground uppercase mb-6 animate-fade-up">
        {isAdmin && paramEntityId ? (
          <>
            <Link to="/entities" className="hover:text-primary transition-colors">Entities</Link>
            <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
            <Link to={`/entities/${paramEntityId}/students`} className="hover:text-primary transition-colors">{entityName || paramEntityId}</Link>
            <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
            <Link to={`/entities/${paramEntityId}/students/${studentId}`} className="hover:text-primary transition-colors">{studentName}</Link>
            <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
            <span className="text-primary" aria-current="page">Contacts</span>
          </>
        ) : (
          <>
            <Link to="/profile" className="hover:text-primary transition-colors">My Profile</Link>
            <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
            <span className="text-primary" aria-current="page">Contacts</span>
          </>
        )}
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">{studentName}'s Contacts</h2>
          <p className="text-muted-foreground mt-1">Manage communication channels and reachability ranks.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2" aria-label="Export contacts as CSV">
            <Download className="w-4 h-4" aria-hidden="true" /> <span className="hidden sm:inline">Export CSV</span>
          </Button>
          <Button className="gap-2" onClick={onAddContact} aria-label="Add a new contact">
            <Plus className="w-4 h-4" aria-hidden="true" /> <span className="hidden sm:inline">New Contact</span>
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm flex flex-wrap items-center gap-4 mb-8 animate-fade-up-delay-1" role="search" aria-label="Filter contacts">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter className="w-4 h-4" aria-hidden="true" />
            <label htmlFor="type-filter" className="hidden sm:inline">Type:</label>
            <select
              id="type-filter"
              value={typeFilter?.toString() ?? ''}
              onChange={(e) => setTypeFilter(e.target.value ? Number(e.target.value) : null)}
              className="bg-surface-low border-none rounded-lg text-sm py-1 pl-2 pr-8 focus:ring-0 cursor-pointer"
              aria-label="Filter by contact type"
            >
              <option value="">All Channels</option>
              {Object.entries(CONTACT_TYPE_NAMES).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block h-6 w-[1px] bg-surface-container" />
          <label className="flex items-center gap-2 cursor-pointer group">
            <div className="relative inline-flex items-center">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} className="sr-only peer" aria-label="Show active contacts only" />
              <div className="w-9 h-5 bg-surface-high rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </div>
            <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground">Active Only</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <div className="relative inline-flex items-center">
              <input type="checkbox" checked={bouncedOnly} onChange={(e) => setBouncedOnly(e.target.checked)} className="sr-only peer" aria-label="Show bounced contacts only" />
              <div className="w-9 h-5 bg-surface-high rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </div>
            <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground">Bounced Only</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="animate-fade-up-delay-2">
        {loading ? (
          <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
            <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" aria-hidden="true" />
            <span className="sr-only">Loading contacts</span>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center shadow-sm" role="status">
            <p className="text-muted-foreground text-sm">No contacts found. Click "New Contact" to create one.</p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
            {/* Mobile grouped layout */}
            <div className="lg:hidden">
              {[...groupedContacts.entries()].map(([typeId, group], gi) => {
                const typeName = CONTACT_TYPE_NAMES[typeId] || `Type ${typeId}`;
                const icon = TYPE_ICONS[typeId];
                const colorClass = TYPE_COLORS[typeId] || 'bg-surface-low text-muted-foreground';
                return (
                  <div key={typeId}>
                    {gi > 0 && <div className="h-px bg-surface-high" />}
                    <div className="sticky top-0 z-10 bg-surface-low/80 backdrop-blur-sm px-4 py-2.5 flex items-center gap-2.5">
                      <div className={`w-6 h-6 rounded-md ${colorClass} flex items-center justify-center shrink-0`}>
                        {icon}
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{typeName}</span>
                      <span className="text-[10px] font-bold text-muted-foreground/60 ml-auto">{group.length}</span>
                    </div>
                    <div className="divide-y divide-surface-low/60">
                      {group.map((contact) => {
                        const value = contact.contactValue.val || contact.contactValue.formattedAddress || '';
                        return (
                          <div key={contact.id} className="px-4 py-3 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                {contact.rank === 1 ? (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-[9px] font-black text-white">1</span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-surface-low text-[9px] font-bold text-muted-foreground">{contact.rank}</span>
                                )}
                                {contact.isActive ? (
                                  <Badge variant="success">Active</Badge>
                                ) : contact.bounced ? (
                                  <Badge variant="destructive">Bounced</Badge>
                                ) : (
                                  <Badge variant="secondary">Inactive</Badge>
                                )}
                                {contact.isRecovery && (
                                  <Badge variant="outline" className="text-[10px]">Recovery</Badge>
                                )}
                              </div>
                              <p className={`text-xs font-mono truncate mt-0.5 ${contact.bounced ? 'text-muted-foreground line-through' : 'text-foreground/70'}`}>{value}</p>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button
                                onClick={() => onEditContact(contact)}
                                className="p-2 hover:bg-surface-low rounded-lg text-muted-foreground hover:text-primary transition-all"
                                aria-label={`Edit ${typeName} contact`}
                              >
                                <Pencil className="w-4 h-4" aria-hidden="true" />
                              </button>
                              {contact.rank !== 1 && (
                                <button
                                  onClick={() => setPrimaryContact(contact.id)}
                                  className="p-2 hover:bg-surface-low rounded-lg text-muted-foreground hover:text-amber-500 transition-all"
                                  aria-label={`Set ${typeName} as primary contact`}
                                >
                                  <Star className="w-4 h-4" aria-hidden="true" />
                                </button>
                              )}
                              <button
                                onClick={() => { if (window.confirm('Delete this contact?')) deleteContact(contact.id); }}
                                className="p-2 hover:bg-surface-low rounded-lg text-muted-foreground hover:text-destructive transition-all"
                                aria-label={`Delete ${typeName} contact`}
                              >
                                <Trash2 className="w-4 h-4" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table layout */}
            <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-surface-low/50">
                  <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Type</TableHead>
                  <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Value</TableHead>
                  <TableHead className="text-center text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Rank</TableHead>
                  <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Status</TableHead>
                  <TableHead className="text-center text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Bounced</TableHead>
                  <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Recovery</TableHead>
                  <TableHead className="text-right text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...groupedContacts.entries()].map(([typeId, group], gi) => {
                  const typeName = CONTACT_TYPE_NAMES[typeId] || `Type ${typeId}`;
                  const icon = TYPE_ICONS[typeId];
                  const colorClass = TYPE_COLORS[typeId] || 'bg-surface-low text-muted-foreground';
                  return (
                    <Fragment key={typeId}>
                      <TableRow className={gi > 0 ? 'border-t-4 border-surface-low' : ''}>
                        <TableCell colSpan={7} className="py-2.5 px-4 bg-surface-low/30">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-6 h-6 rounded-md ${colorClass} flex items-center justify-center`}>
                              {icon}
                            </div>
                            <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{typeName}</span>
                            <span className="text-[10px] font-bold text-muted-foreground/50">{group.length} {group.length === 1 ? 'record' : 'records'}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      {group.map((contact) => (
                        <ContactCard key={contact.id} contact={contact} onEdit={onEditContact} onDelete={deleteContact} onSetPrimary={setPrimaryContact} />
                      ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <div className="px-6 py-4 bg-surface-low/30 flex items-center justify-between" style={{ borderTop: '1px solid hsl(210 12% 96%)' }}>
              <span className="text-sm text-muted-foreground font-medium">
                Showing <span className="text-foreground">{filteredContacts.length}</span> of <span className="text-foreground">{contacts.length}</span> contact records
                {groupedContacts.size > 1 && <span className="text-muted-foreground/60"> across {groupedContacts.size} types</span>}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
